import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { motion } from 'framer-motion'
import { RiWaterFlashFill } from 'react-icons/ri'
import { Icon } from './Icon'
import { Navigation } from './Navigation'
import { FiMenu } from 'react-icons/fi'
import { Drawer } from './Drawer'

const HeaderContainer = styled(motion.header)<{ $scrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: #000000;
  padding: 0.5rem 0;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
  width: 100%;
`

const NavContainer = styled(motion.div)`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 0.5rem;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  width: 100%;

  @media (min-width: 491px) {
    padding: 0 1rem;
  }
`

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  z-index: 1001;
  flex-shrink: 0;
  gap: 0.5rem;
`

const LogoIcon = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    filter: brightness(1.2);
    transform: scale(1.1);
  }
`

const LogoText = styled.span`
  color: #fff;
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  background: linear-gradient(45deg, #4ecdc4, #ff6b6b);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
  white-space: nowrap;

  @media (min-width: 491px) {
    font-size: 1.5rem;
  }
`

const DesktopNav = styled.div`
  display: none;
  @media (min-width: 491px) {
    display: flex;
    align-items: center;
    gap: 2rem;
  }
`

const MobileNav = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
  @media (min-width: 491px) {
    display: none;
  }
`

const MobileLogoText = styled(LogoText)`
  margin-left: 0.5rem;
`

const MenuButton = styled.button`
  background: none;
  border: none;
  color: #fff;
  font-size: 2rem;
  display: flex;
  align-items: center;
  cursor: pointer;
`

const StyledLink = styled(Link)`
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const DesktopOnly = styled.div`
  display: none;
  @media (min-width: 491px) {
    display: flex;
    align-items: center;
    width: 100%;
    justify-content: space-between;
  }
`

const navVariants = {
  hidden: { y: -100, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.5, ease: 'easeOut' } },
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 },
  },
}

const logoVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.5 } },
  hover: { scale: 1.05, transition: { duration: 0.2 } },
}

export const Header: React.FC = () => {
  const [scrolled, setScrolled] = useState(false)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      if (scrollTop > 50) {
        setScrolled(true)
      } else {
        setScrolled(false)
      }
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen)
  }

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isDrawerOpen])

  return (
    <>
      <HeaderContainer
        $scrolled={scrolled}
        variants={navVariants}
        initial='hidden'
        animate='visible'>
        <NavContainer variants={containerVariants}>
          {/* Mobile view (below 490px) */}
          <MobileNav>
            <StyledLink to='/'>
              <LogoContainer>
                <LogoIcon variants={logoVariants} whileHover='hover'>
                  <Icon icon={RiWaterFlashFill} size={28} color='#4ecdc4' />
                </LogoIcon>
                <MobileLogoText>Mr. Zee</MobileLogoText>
              </LogoContainer>
            </StyledLink>
            <MenuButton onClick={toggleDrawer} aria-label='Open menu'>
              <Icon icon={FiMenu} size={28} color='#fff' />
            </MenuButton>
          </MobileNav>

          {/* Desktop view (491px and above) */}
          <DesktopOnly>
            <StyledLink to='/'>
              <LogoContainer>
                <LogoIcon variants={logoVariants} whileHover='hover'>
                  <Icon icon={RiWaterFlashFill} size={28} color='#4ecdc4' />
                </LogoIcon>
                <LogoText>Mr. Zee</LogoText>
              </LogoContainer>
            </StyledLink>
            <DesktopNav>
              <Navigation />
            </DesktopNav>
          </DesktopOnly>
        </NavContainer>
      </HeaderContainer>
      <Drawer isOpen={isDrawerOpen} onClose={toggleDrawer} />
    </>
  )
}
